<template>
    <div>
        <!-- Hero -->
        <section class="relative pt-32 overflow-hidden">
            <div class="absolute inset-0 bg-hero-grid bg-size-[18px_18px]" />
            <div class="-top-40 -left-40 absolute bg-primary/10 blur-3xl rounded-full w-md h-112" />
            <div class="-right-40 -bottom-40 absolute bg-primary/10 blur-3xl rounded-full w-md h-112" />

            <div class="relative mx-auto px-4 md:px-6 py-16 md:py-24 max-w-6xl">
                <div class="md:items-center gap-10 grid md:grid-cols-5">
                    <div class="space-y-6 md:col-span-3">
                        <h1 class="font-bold text-ink text-3xl md:text-5xl leading-tight tracking-tight">{{ $t("general.about.hero.title") }}</h1>
                        <p class="text-ink/75 text-base md:text-lg lead">{{ $t("general.about.hero.description") }}</p>
                        <div class="flex flex-wrap items-center gap-3">
                            <Button variant="secondary">
                                <NuxtLink href="#about">{{ $t("general.about.hero.learn_more") }}</NuxtLink>
                            </Button>
                            <Button>
                                <NuxtLink href="#assure">{{ $t("general.about.hero.verify_product") }}</NuxtLink>
                            </Button>
                        </div>
                    </div>
                    <div class="md:col-span-2">
                        <div class="relative bg-white shadow-soft p-6 border border-zinc-200 rounded-3xl h-56 md:h-72 lg:h-80">
                            <div class="absolute inset-0 bg-linear-to-br from-white via-white to-primary/5 rounded-3xl" />
                            <div class="relative place-items-center grid w-full h-full">
                                <Logo />
                                <p class="mt-4 text-ink/60 text-sm">{{ $t("general.about.hero.signature_caption") }}</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>

        <!-- About section -->
        <section id="about" class="mx-auto px-4 md:px-6 py-14 max-w-6xl">
            <div class="gap-8 grid md:grid-cols-12">
                <div class="md:col-span-5">
                    <h2 class="font-extrabold text-ink text-2xl md:text-3xl tracking-tight">{{ $t("general.about.section.title") }}</h2>
                    <div class="bg-primary mt-4 rounded-full w-24 h-1" />
                </div>
                <div class="md:col-span-7 text-ink/80 leading-8">
                    <p>
                        <strong>{{ $t("general.about.brand_name") }}</strong>
                        {{ $t("general.about.section.definition") }}
                    </p>
                    <p class="mt-4">
                        {{ $t("general.about.section.detail_1") }}
                        <em>{{ $t("general.about.section.detail_em_1") }}</em>
                        {{ $t("general.about.section.detail_2") }}
                        <em>{{ $t("general.about.section.detail_em_2") }}</em>
                        {{ $t("general.about.section.detail_3") }}
                    </p>
                </div>
            </div>
        </section>

        <!-- Quality section -->
        <section id="craft" class="bg-white">
            <div class="mx-auto px-4 md:px-6 py-14 max-w-6xl">
                <div class="items-start gap-8 grid md:grid-cols-12">
                    <div class="md:col-span-5">
                        <h3 class="font-extrabold text-ink text-2xl md:text-3xl tracking-tight">{{ $t("general.about.quality.title") }}</h3>
                        <div class="bg-primary mt-4 rounded-full w-24 h-1" />
                    </div>
                    <div class="md:col-span-7 text-ink/80 leading-8">
                        <p>{{ $t("general.about.quality.description") }}</p>
                    </div>
                </div>

                <!-- Highlights -->
                <div class="gap-6 grid sm:grid-cols-2 lg:grid-cols-3 mt-8">
                    <div class="bg-linear-to-br from-white to-amber-50/40 shadow-soft p-6 border border-zinc-200 rounded-2xl">
                        <div class="inline-flex justify-center items-center bg-primary/15 mb-4 rounded-xl w-12 h-12">
                            <span class="text-primary" aria-hidden>◆</span>
                        </div>
                        <h4 class="font-semibold text-ink">{{ $t("general.about.cards.alloy.title") }}</h4>
                        <p class="mt-2 text-ink/70 text-sm leading-6">{{ $t("general.about.cards.alloy.description") }}</p>
                    </div>
                    <div class="bg-linear-to-br from-white to-amber-50/40 shadow-soft p-6 border border-zinc-200 rounded-2xl">
                        <div class="inline-flex justify-center items-center bg-primary/15 mb-4 rounded-xl w-12 h-12">
                            <span class="text-primary" aria-hidden>◎</span>
                        </div>
                        <h4 class="font-semibold text-ink">{{ $t("general.about.cards.engineering.title") }}</h4>
                        <p class="mt-2 text-ink/70 text-sm leading-6">{{ $t("general.about.cards.engineering.description") }}</p>
                    </div>
                    <div class="bg-linear-to-br from-white to-amber-50/40 shadow-soft p-6 border border-zinc-200 rounded-2xl">
                        <div class="inline-flex justify-center items-center bg-primary/15 mb-4 rounded-xl w-12 h-12">
                            <span class="text-primary" aria-hidden>▣</span>
                        </div>
                        <h4 class="font-semibold text-ink">{{ $t("general.about.cards.quality_control.title") }}</h4>
                        <p class="mt-2 text-ink/70 text-sm leading-6">{{ $t("general.about.cards.quality_control.description") }}</p>
                    </div>
                </div>
            </div>
        </section>

        <!-- Assurance section -->
        <section id="assure" class="bg-zinc-100/60">
            <div class="mx-auto px-4 md:px-6 py-14 max-w-6xl">
                <div class="items-start gap-8 grid md:grid-cols-12">
                    <div class="md:col-span-5">
                        <h3 class="font-extrabold text-ink text-2xl md:text-3xl tracking-tight">{{ $t("general.about.assurance.title") }}</h3>
                        <div class="bg-primary mt-4 rounded-full w-24 h-1" />
                    </div>
                    <div class="md:col-span-7 text-ink/80 leading-8">
                        <p>{{ $t("general.about.assurance.description") }}</p>
                    </div>
                </div>

                <!-- Verification widget -->
                <div class="bg-white shadow-soft mt-8 p-6 border border-zinc-200 rounded-2xl">
                    <div class="md:items-center gap-4 grid md:grid-cols-[1fr_auto]">
                        <label for="laser" class="block font-medium text-ink/80 text-sm">{{ $t("general.about.assurance.laser_input_label") }}</label>
                        <div class="hidden md:block" />
                        <Form :model="form" class="w-full" @submit="onSubmit">
                            <div class="flex gap-4">
                                <FormField name="code" :label="$t('verify.verify.input.label')" class="w-full">
                                    <Input
                                        id="laser"
                                        v-model="form.code"
                                        type="text"
                                        :placeholder="$t('verify.verify.input.placeholder')"
                                        class="w-full placeholder:text-white placeholder:text-center"
                                        autocomplete="off"
                                    />
                                </FormField>
                                <Button type="submit" class="text-white lg:px-26">{{ $t("verify.verify.button.verify") }}</Button>
                            </div>
                        </Form>
                    </div>
                    <p class="mt-3 text-ink/60 text-xs">{{ $t("general.about.assurance.image_note") }}</p>
                </div>
            </div>
        </section>

        <!-- Stockists -->
        <section id="stockists" class="bg-white">
            <div class="mx-auto px-4 md:px-6 py-14 max-w-6xl">
                <div class="items-start gap-8 grid md:grid-cols-12">
                    <div class="md:col-span-5">
                        <h3 class="font-extrabold text-ink text-2xl md:text-3xl tracking-tight">{{ $t("general.about.experience.title") }}</h3>
                        <div class="bg-primary mt-4 rounded-full w-24 h-1" />
                    </div>
                    <div class="md:col-span-7 text-ink/80 leading-8">
                        <p>{{ $t("general.about.experience.description") }}</p>
                    </div>
                </div>

                <!-- Info cards -->
                <div class="gap-6 grid sm:grid-cols-2 lg:grid-cols-3 mt-8">
                    <div class="bg-zinc-50 p-6 border border-zinc-200 rounded-2xl">
                        <h4 class="font-semibold text-ink">{{ $t("general.about.cards.showcase.title") }}</h4>
                        <p class="mt-2 text-ink/70 text-sm">{{ $t("general.about.cards.showcase.description") }}</p>
                    </div>
                    <div class="bg-zinc-50 p-6 border border-zinc-200 rounded-2xl">
                        <h4 class="font-semibold text-ink">{{ $t("general.about.cards.verification.title") }}</h4>
                        <p class="mt-2 text-ink/70 text-sm">{{ $t("general.about.cards.verification.description") }}</p>
                    </div>
                    <div class="bg-zinc-50 p-6 border border-zinc-200 rounded-2xl">
                        <h4 class="font-semibold text-ink">{{ $t("general.about.cards.in_store.title") }}</h4>
                        <p class="mt-2 text-ink/70 text-sm">{{ $t("general.about.cards.in_store.description") }}</p>
                    </div>
                </div>
            </div>
        </section>

        <!-- Summary -->
        <section class="relative overflow-hidden">
            <div class="absolute inset-0 bg-hero-grid bg-size-[18px_18px]" />
            <div class="relative mx-auto px-4 md:px-6 py-16 max-w-6xl">
                <div class="bg-white/90 shadow-soft p-8 md:p-12 border border-zinc-200 rounded-3xl">
                    <div class="mx-auto max-w-3xl text-center">
                        <h3 class="font-extrabold text-ink text-2xl md:text-3xl tracking-tight">{{ $t("general.about.summary.title") }}</h3>
                        <p class="mt-4 text-ink/75 leading-8">{{ $t("general.about.summary.description") }}</p>
                    </div>
                </div>
            </div>
        </section>
    </div>
</template>

<script lang="ts" setup>
import { Form, FormField } from "~/components/ui/form";
import Input from "@/components/ui/input/Input.vue";
import Button from "~/components/ui/button/Button.vue";

const form = reactive({
    code: "",
});

async function onSubmit() {
    navigateTo(`/verify?code=${form.code}`);
}
</script>

<style scoped>
.bg-hero-grid {
    background-image: radial-gradient(circle at 1px 1px, rgba(200, 162, 77, 0.25) 1px, transparent 1px);
}
</style>
